import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { getRank } from '../data/initialState';
import { t } from '../data/translations';
import { supabase } from '../supabase';
import FamilyGroup from './FamilyGroup';

export default function TabSosial() {
  const { state, dispatch } = useApp();
  const lang = state.language || 'en';
  const [tab, setTab] = useState('global');
  const [globalTop, setGlobalTop] = useState([]);
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState(state.user?.username || state.user?.name || state.user?.email?.split('@')[0] || '');
  const displayName = username || state.user?.name || state.user?.email?.split('@')[0] || '';
  const [settingUsername, setSettingUsername] = useState(false);
  const [usernameInput, setUsernameInput] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [myScore, setMyScore] = useState(0);
  const [myRank, setMyRank] = useState(null);
  const [referrals, setReferrals] = React.useState([]);
  const [copied, setCopied] = React.useState(false);
  
  const myCode = state.referralCode || (state.user?.name || 'USER').toUpperCase().replace(/[^A-Z0-9]/g,'').slice(0,6) + '42';
  const referralLink = `https://mysavely.app?ref=${myCode}`;
  const referralCount = state.referralCount || 0;
  
  const rewards = [
    { count:10, days:7, label: lang==='en'?'🎁 Invite 10 friends → 7 days Advanced FREE':lang==='sv'?'🎁 Bjud in 10 vänner → 7 dagar Advanced GRATIS':lang==='da'?'🎁 Inviter 10 venner → 7 dage Advanced GRATIS':'🎁 Inviter 10 venner → 7 dager Advanced GRATIS' },
  ];
  const nextReward = rewards.find(r => r.count > referralCount);
  
  const copyCode = () => {
    try { navigator.clipboard.writeText(referralLink); } catch(e) {}
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  const shareLink = () => {
    const text = lang==='en'?`Join Savely with my code ${myCode}: ${referralLink}`:lang==='sv'?`Gå med på Savely med min kod ${myCode}: ${referralLink}`:lang==='da'?`Bliv med på Savely med min kode ${myCode}: ${referralLink}`:`Bli med på Savely med koden min ${myCode}: ${referralLink}`;
    if (navigator.share) { navigator.share({ title:'Savely', text, url:referralLink }); }
    else { copyCode(); }
  };

  useEffect(() => {
    loadGlobal();
    loadFriends();
    loadFriendRequests();
    checkUsername();
    updateMyScore();
  }, []);

  const updateMyScore = async () => {
    if (!state.user?.id) return;
    const score = Math.round((state.streak || 0));
    await supabase.from('profiles').update({ streak: state.streak || 0 }).eq('id', state.user?.id);
    setMyScore(score);
  };

  const checkUsername = async () => {
    if (!state.user?.id) return;
    const { data } = await supabase.from('profiles').select('username, avatar_emoji').eq('id', state.user.id).single();
    if (data?.username) setUsername(data.username);
  };

  const loadGlobal = async () => {
    const { data } = await supabase
      .from('profiles')
      .select('id, name, username, score, level, streak, avatar_emoji')
      .eq('is_public', true)
      .not('username', 'is', null)
      .order('streak', { ascending: false })
      .limit(20);
    if (data) {
      setGlobalTop(data);
      const rank = data.findIndex(p => p.id === state.user?.id);
      if (rank >= 0) setMyRank(rank + 1);
    }
  };

  const loadFriends = async () => {
    if (!state.user?.id) return;
    const { data } = await supabase
      .from('friendships')
      .select(`
        id, status,
        requester:requester_id(id, name, username, score, level, streak, avatar_emoji),
        receiver:receiver_id(id, name, username, score, level, streak, avatar_emoji)
      `)
      .or(`requester_id.eq.${state.user.id},receiver_id.eq.${state.user.id}`)
      .eq('status', 'accepted');
    if (data) {
      const friendList = data.map(f => {
        const friend = f.requester?.id === state.user.id ? f.receiver : f.requester;
        return { ...friend, friendshipId: f.id };
      });
      setFriends(friendList.sort((a, b) => (b.score || 0) - (a.score || 0)));
    }
  };

  const loadFriendRequests = async () => {
    if (!state.user?.id) return;
    const { data } = await supabase
      .from('friendships')
      .select(`id, requester:requester_id(id, name, username, avatar_emoji)`)
      .eq('receiver_id', state.user.id)
      .eq('status', 'pending');
    if (data) setFriendRequests(data);
  };

  const searchUsers = async () => {
    if (!search.trim()) return;
    setLoading(true);
    const { data } = await supabase
      .from('profiles')
      .select('id, name, username, score, level, avatar_emoji')
      .ilike('username', `%${search}%`)
      .eq('is_public', true)
      .neq('id', state.user?.id)
      .limit(10);
    setSearchResults(data || []);
    setLoading(false);
  };

  const sendFriendRequest = async (receiverId) => {
    await supabase.from('friendships').insert({ requester_id: state.user.id, receiver_id: receiverId });
    setSearchResults(prev => prev.filter(u => u.id !== receiverId));
    alert(t(lang,'requestSent'));
  };

  const acceptRequest = async (friendshipId) => {
    await supabase.from('friendships').update({ status: 'accepted' }).eq('id', friendshipId);
    loadFriendRequests();
    loadFriends();
  };

  const rejectRequest = async (friendshipId) => {
    await supabase.from('friendships').delete().eq('id', friendshipId);
    loadFriendRequests();
  };

  const saveUsername = async () => {
    if (!usernameInput.trim()) { setUsernameError('Skriv inn et brukernavn'); return; }
    if (usernameInput.length < 3) { setUsernameError(t(lang,'minChars')); return; }
    if (!/^[a-zA-Z0-9_]+$/.test(usernameInput)) { setUsernameError(t(lang,'onlyChars')); return; }
    const { error } = await supabase.from('profiles').update({ username: usernameInput.toLowerCase() }).eq('id', state.user.id);
    if (error?.code === '23505') { setUsernameError(t(lang,'usernameTaken')); return; }
    setUsername(usernameInput.toLowerCase());
    setSettingUsername(false);
    setUsernameError('');
  };

  const fmt = n => Math.round(Math.abs(n || 0)).toLocaleString(lang==='en'?'en-US':lang==='sv'?'sv-SE':lang==='da'?'da-DK':'nb-NO');

  const EMOJIS = ['🌱','🦁','🐯','🦊','🐺','🦅','🐬','🦋','⭐','🔥','💎','🚀','🎯','🏆','💪'];

  const RankCard = ({ user, rank, isMe }) => (
    <div style={{ display:'flex', alignItems:'center', gap:12, padding:'0.875rem', background: isMe ? 'var(--g50)' : 'var(--card)', border: `1.5px solid ${isMe ? 'var(--g400)' : 'var(--border)'}`, borderRadius:'var(--r-lg)', marginBottom:8 }}>
      <div style={{ fontFamily:'var(--font-display)', fontSize:18, fontWeight:800, color: rank <= 3 ? ['#FFD700','#C0C0C0','#CD7F32'][rank-1] : 'var(--ink3)', minWidth:40, textAlign:'center' }}>
        {rank <= 3 ? ['🥇','🥈','🥉'][rank-1] : `#${rank}`}
      </div>
      <div style={{ fontSize:28 }}>{user.avatar_emoji || '🌱'}</div>
      <div style={{ flex:1 }}>
        <div style={{ fontSize:14, fontWeight:700, color:'var(--ink)' }}>{isMe ? t(lang,'yourUsername') : (user.username || user.name || 'Ukjent')}</div>
        <div style={{ fontSize:11, color:'var(--ink3)' }}>Lv.{user.level || 1} • {user.streak || 0} {lang==='en'?'days streak':lang==='sv'?'dagars streak':lang==='da'?'dages streak':'dager streak'}</div>
      </div>
      <div style={{ textAlign:'right' }}>
        <div style={{ fontFamily:'var(--font-display)', fontSize:15, fontWeight:800, color:'var(--g400)' }}>{fmt(user.streak || 0)}</div>
        <div style={{ fontSize:10, color:'var(--ink3)' }}>{lang==='en'?'points':lang==='sv'?'poäng':lang==='da'?'point':'poeng'}</div>
      </div>
    </div>
  );

  if (!displayName && !settingUsername) {
    return (
      <div style={{ padding:'1.5rem', textAlign:'center' }}>
      {/* Referral Section */}
      <div style={{ background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:'var(--r-lg)', padding:'1.25rem', marginBottom:'1rem' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
          <div style={{ fontSize:24 }}>🎁</div>
          <div>
            <div style={{ fontSize:15, fontWeight:800, color:'var(--ink)', fontFamily:'var(--font-display)' }}>{lang==='en'?'Invite friends':lang==='sv'?'Bjud in vänner':lang==='da'?'Inviter venner':'Inviter venner'}</div>
            <div style={{ fontSize:11, color:'var(--ink3)' }}>{lang==='en'?'Earn free Advanced':lang==='sv'?'Tjäna gratis Advanced':lang==='da'?'Tjen gratis Advanced':'Tjen gratis Advanced'}</div>
          </div>
          {state.ambassador && <div style={{ marginLeft:'auto', background:'linear-gradient(135deg,#F5C518,#FF9500)', borderRadius:20, padding:'3px 10px', fontSize:11, fontWeight:700, color:'#fff' }}>🏅 Ambassador</div>}
        </div>
        {nextReward && (
          <div style={{ marginBottom:10 }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
              <span style={{ fontSize:11, color:'var(--ink3)' }}>{referralCount}/{nextReward.count} {lang==='en'?'friends':lang==='sv'?'vänner':lang==='da'?'venner':'venner'}</span>
              <span style={{ fontSize:11, color:'var(--g600)', fontWeight:700 }}>{nextReward.label}</span>
            </div>
            <div style={{ background:'var(--surface)', borderRadius:99, height:6 }}>
              <div style={{ width:`${Math.min(100,(referralCount/nextReward.count)*100)}%`, height:'100%', background:'var(--g400)', borderRadius:99 }}/>
            </div>
          </div>
        )}
        <div style={{ display:'flex', gap:6, marginBottom:10 }}>
          {rewards.map(r => (
            <div key={r.count} style={{ flex:1, textAlign:'center', padding:'6px 4px', background:referralCount>=r.count?'var(--g50)':'var(--surface)', borderRadius:'var(--r-md)', border:`1px solid ${referralCount>=r.count?'var(--g200)':'var(--border)'}` }}>
              <div style={{ fontSize:12 }}>{referralCount>=r.count?'✅':'🔒'}</div>
              <div style={{ fontSize:10, color:referralCount>=r.count?'var(--g600)':'var(--ink4)', fontWeight:700 }}>{r.count}</div>
            </div>
          ))}
        </div>
        <div style={{ background:'var(--surface)', borderRadius:'var(--r-md)', padding:'8px 12px', display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
          <div>
            <div style={{ fontSize:10, color:'var(--ink4)' }}>{lang==='en'?'Your code':lang==='sv'?'Din kod':lang==='da'?'Din kode':'Din kode'}</div>
            <div style={{ fontSize:18, fontWeight:800, color:'var(--g400)', letterSpacing:2 }}>{myCode}</div>
          </div>
          <button onClick={copyCode} style={{ padding:'6px 14px', background:copied?'var(--g400)':'var(--card)', border:'1.5px solid var(--border)', borderRadius:'var(--r-md)', fontSize:12, fontWeight:700, color:copied?'var(--ink)':'var(--ink2)', cursor:'pointer' }}>
            {copied?'✅':lang==='en'?'Copy':lang==='sv'?'Kopiera':lang==='da'?'Kopiér':'Kopier'}
          </button>
        </div>
        <button onClick={shareLink} style={{ width:'100%', padding:'12px 0', background:'var(--g400)', color:'var(--ink)', border:'none', borderRadius:'var(--r-full)', fontSize:14, fontWeight:800, cursor:'pointer' }}>
          {lang==='en'?'Share link 🚀':lang==='sv'?'Dela länk 🚀':lang==='da'?'Del link 🚀':'Del lenke 🚀'}
        </button>
        {referralCount > 0 && <div style={{ textAlign:'center', marginTop:8, fontSize:12, color:'var(--ink3)' }}>🎉 {referralCount} {lang==='en'?'friends joined':lang==='sv'?'vänner gick med':lang==='da'?'venner tilmeldte sig':'venner ble med'}!</div>}
      </div>
        <div style={{ fontSize:48, marginBottom:16 }}>👥</div>
        <div style={{ fontFamily:'var(--font-display)', fontSize:20, fontWeight:800, color:'var(--ink)', marginBottom:8 }}>{t(lang,'chooseUsername')}</div>
        <div style={{ fontSize:14, color:'var(--ink2)', marginBottom:24 }}>{lang==='en'?'You need a username to join the leaderboard':lang==='sv'?'Du behöver ett användarnamn':lang==='da'?'Du skal have et brugernavn':'Du trenger et brukernavn'} for å delta på leaderboardet og legge til venner!</div>
        <button onClick={() => setSettingUsername(true)} style={{ padding:'12px 24px', background:'var(--g400)', color:'var(--ink)', border:'none', borderRadius:'var(--r-full)', fontSize:15, fontWeight:700, cursor:'pointer' }}>
          Velg brukernavn →
        </button>
      </div>
    );
  }

  if (settingUsername) {
    return (
      <div style={{ padding:'1.5rem' }}>
        <div style={{ fontFamily:'var(--font-display)', fontSize:18, fontWeight:800, color:'var(--ink)', marginBottom:16 }}>{t(lang,'chooseUsername')}</div>
        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:13, color:'var(--ink2)', marginBottom:8 }}>{t(lang,'chooseAvatar')}:</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
            {EMOJIS.map(e => (
              <button key={e} onClick={async () => { await supabase.from('profiles').update({ avatar_emoji: e }).eq('id', state.user.id); }} style={{ fontSize:24, background:'var(--surface)', border:'1.5px solid var(--border)', borderRadius:'var(--r-md)', padding:'6px 8px', cursor:'pointer' }}>{e}</button>
            ))}
          </div>
        </div>
        <input
          value={usernameInput}
          onChange={e => setUsernameInput(e.target.value)}
          placeholder="brukernavn (kun bokstaver, tall, _)"
          style={{ width:'100%', padding:'12px 14px', borderRadius:'var(--r-md)', border:'1.5px solid var(--border2)', fontSize:15, marginBottom:8, background:'var(--surface)', color:'var(--ink)' }}
        />
        {usernameError && <div style={{ color:'var(--r400)', fontSize:12, marginBottom:8 }}>{usernameError}</div>}
        <button onClick={saveUsername} style={{ width:'100%', padding:12, background:'var(--g400)', color:'var(--ink)', border:'none', borderRadius:'var(--r-lg)', fontSize:15, fontWeight:700, cursor:'pointer' }}>
          {lang==='en'?'Save username →':lang==='sv'?'Spara användarnamn →':lang==='da'?'Gem brugernavn →':'Lagre brukernavn →'}
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding:'1rem 1rem 2rem' }}>

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontFamily:'var(--font-display)', fontSize:22, fontWeight:800, color:'var(--ink)' }}>
          🏆 {lang==='en'?'Social':lang==='sv'?'Socialt':lang==='da'?'Socialt':'Sosialt'}
        </div>
        <div style={{ background:'rgba(34,201,122,0.1)', border:'1.5px solid rgba(34,201,122,0.3)', borderRadius:999, padding:'6px 16px', fontSize:14, fontWeight:800, color:'var(--g400)' }}>
          @{displayName || username}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:6, marginBottom:16, overflowX:'auto' }}>
        {(lang==='en'?[['global','🌍 Global'],['friends','👥 Friends'],['family','👨‍👩‍👧 Family'],['referral','🎁 Invite']]:
          lang==='sv'?[['global','🌍 Global'],['friends','👥 Vänner'],['family','👨‍👩‍👧 Familj'],['referral','🎁 Bjud in']]:
          lang==='da'?[['global','🌍 Global'],['friends','👥 Venner'],['family','👨‍👩‍👧 Familie'],['referral','🎁 Inviter']]:
          [['global','🌍 Global'],['friends','👥 Venner'],['family','👨‍👩‍👧 Familie'],['referral','🎁 Inviter']])
          .map(([id,label]) => (
          <button key={id} onClick={() => setTab(id)} style={{
            padding:'8px 14px', borderRadius:'var(--r-full)', fontSize:12, fontWeight:700,
            border: tab===id ? 'none' : '1px solid var(--border)',
            background: tab===id ? 'var(--ink)' : 'var(--surface)',
            color: tab===id ? 'var(--paper)' : 'var(--ink3)',
            cursor:'pointer', whiteSpace:'nowrap', flexShrink:0,
          }}>{label}</button>
        ))}
      </div>

      {/* Min score */}
      <div style={{ background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:'var(--r-lg)', padding:'1rem', marginBottom:12, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div>
          <div style={{ fontSize:12, color:'var(--ink3)', marginBottom:2 }}>{lang==='en'?'Your score':lang==='sv'?'Din poäng':lang==='da'?'Din score':'Din score'}</div>
          <div style={{ fontFamily:'var(--font-display)', fontSize:24, fontWeight:800, color:'var(--ink)' }}>{state.streak || 0}</div>
          <div style={{ fontSize:10, color:'var(--ink4)' }}>{lang==='en'?'streak days':lang==='sv'?'streak dagar':lang==='da'?'streak dage':'streak dager'}</div>
        </div>
      </div>

      {/* GLOBAL TAB */}
      {tab === 'global' && (
        <div>
          <div style={{ fontFamily:'var(--font-display)', fontSize:14, fontWeight:700, color:'var(--ink)', marginBottom:10 }}>
            🌍 {lang==='en'?'Global leaderboard':lang==='sv'?'Global leaderboard':lang==='da'?'Globalt leaderboard':'Globalt leaderboard'}
          </div>
          {globalTop.length === 0 ? (
            <div style={{ textAlign:'center', padding:'2rem', color:'var(--ink3)', fontSize:13 }}>
              {lang==='en'?'No users yet!':lang==='sv'?'Inga användare ännu!':lang==='da'?'Ingen brugere endnu!':'Ingen brukere ennå!'}
            </div>
          ) : globalTop.map((user, i) => (
            <RankCard key={user.id} user={user} rank={i+1} isMe={user.id === state.user?.id} lang={lang}/>
          ))}
        </div>
      )}

      {/* FRIENDS TAB */}
      {tab === 'friends' && (
        <div>
          <div style={{ fontFamily:'var(--font-display)', fontSize:14, fontWeight:700, color:'var(--ink)', marginBottom:10 }}>
            👥 {lang==='en'?'Friends':lang==='sv'?'Vänner':lang==='da'?'Venner':'Venner'}
          </div>
          {(friends || []).length === 0 ? (
            <div style={{ textAlign:'center', padding:'2rem', color:'var(--ink3)', fontSize:13 }}>
              {lang==='en'?'No friends yet — invite someone!':lang==='sv'?'Inga vänner ännu!':lang==='da'?'Ingen venner endnu!':'Ingen venner ennå — inviter noen!'}
            </div>
          ) : (friends || []).map((user, i) => (
            <RankCard key={user.id} user={user} rank={i+1} isMe={false} lang={lang}/>
          ))}
        </div>
      )}

      {/* FAMILY TAB */}
      {tab === 'family' && (
        <FamilyGroup lang={lang} state={state} dispatch={dispatch}/>
      )}

      {/* REFERRAL TAB */}
      {tab === 'referral' && (
        <div>
          <div style={{ background:'linear-gradient(135deg, rgba(34,201,122,0.15), rgba(0,212,255,0.1))', border:'2px solid rgba(34,201,122,0.4)', borderRadius:'var(--r-lg)', padding:'1.25rem' }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
              <div className="savvy-bounce" style={{ flexShrink:0 }}>
                <img src="/mascot/savvy-excited.png" alt="Savvy" style={{ width:56, height:56, objectFit:'contain' }}/>
              </div>
              <div>
                <div style={{ fontFamily:'var(--font-display)', fontSize:16, fontWeight:800, color:'var(--g400)' }}>
                  🎁 {lang==='en'?'Invite friends!':lang==='sv'?'Bjud in vänner!':lang==='da'?'Inviter venner!':'Inviter venner!'}
                </div>
                <div style={{ fontSize:12, color:'var(--ink3)', marginTop:2 }}>
                  {lang==='en'?'Earn free Advanced days!':lang==='sv'?'Tjäna gratis Advanced-dagar!':lang==='da'?'Optjen gratis Advanced-dage!':'Tjen gratis Advanced-dager!'}
                </div>
              </div>
            </div>

            <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:14 }}>
              {rewards.map((r, i) => {
                const done = referralCount >= r.count;
                return (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', borderRadius:'var(--r-md)', background: done ? 'rgba(34,201,122,0.15)' : 'var(--surface)', border:`1px solid ${done ? 'var(--g200)' : 'var(--border)'}` }}>
                    <div style={{ fontSize:18 }}>{done ? '✅' : `${r.count}👥`}</div>
                    <div style={{ flex:1, fontSize:12, fontWeight:600, color: done ? 'var(--g600)' : 'var(--ink2)' }}>{r.label}</div>
                  </div>
                );
              })}
            </div>

            <div style={{ background:'var(--card)', borderRadius:'var(--r-md)', padding:'10px 14px', marginBottom:10, display:'flex', justifyContent:'space-between', alignItems:'center', border:'1px solid var(--border)' }}>
              <div>
                <div style={{ fontSize:10, color:'var(--ink3)', marginBottom:2 }}>{lang==='en'?'Your code':lang==='sv'?'Din kod':lang==='da'?'Din kode':'Din kode'}</div>
                <div style={{ fontFamily:'var(--font-display)', fontSize:22, fontWeight:800, color:'var(--g400)', letterSpacing:3 }}>{myCode}</div>
              </div>
              <button onClick={() => { try { navigator.clipboard.writeText(referralLink); setCopied(true); setTimeout(()=>setCopied(false),2000); } catch(e){} }}
                style={{ padding:'8px 16px', borderRadius:'var(--r-full)', background: copied ? 'var(--g400)' : 'var(--surface)', border:'1.5px solid var(--g400)', color: copied ? 'var(--ink)' : 'var(--g400)', fontSize:13, fontWeight:700, cursor:'pointer' }}>
                {copied ? '✅' : (lang==='en'?'Copy':lang==='sv'?'Kopiera':lang==='da'?'Kopiér':'Kopier')}
              </button>
            </div>

            <button onClick={async () => {
              const text = lang==='en'?`Join me on Savely! Code: ${myCode} ${referralLink}`:lang==='sv'?`Gå med mig på Savely! Kod: ${myCode} ${referralLink}`:lang==='da'?`Tilmeld dig Savely! Kode: ${myCode} ${referralLink}`:`Bli med på Savely! Kode: ${myCode} ${referralLink}`;
              try {
                if (navigator.share) await navigator.share({ title:'Savely', text, url: referralLink });
                else { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(()=>setCopied(false),2000); }
              } catch(e) {}
            }} style={{ width:'100%', padding:'12px', borderRadius:'var(--r-lg)', background:'var(--g400)', color:'var(--ink)', border:'none', fontFamily:'var(--font-display)', fontSize:14, fontWeight:800, cursor:'pointer' }}>
              📤 {lang==='en'?'Share with friends':lang==='sv'?'Dela med vänner':lang==='da'?'Del med venner':'Del med venner'}
            </button>
          </div>
        </div>
      )}

      <div style={{ height:16 }}/>
    </div>
  );
}
